import React, { Component } from "react";
import Dropzone from "react-dropzone";
import { sendFileAttachment } from "../../api/secure-messaging-api";
import config from "../../config";
import culture from "../../constants/culture-codes";
import link from "../../constants/links";
import translate from "../../constants/translations";
import type { CustomerContext } from "../../types";
import type { UploadItem } from "./upload-progress-bar";
import UploadProgressBars from "./upload-progress-bars";

interface UploadedAttachmentResponse {
	AttachmentId: string;
}

export type { UploadedAttachmentResponse };

interface FileRejectionLike {
	file?: File;
}

interface FileAttachmentDropDownProps {
	cultureCode: string;
	customer: CustomerContext;
	attachmentsDisabled: boolean;
	updateUploads: (uploads: UploadItem[]) => void;
	updateAttachments: (attachments: unknown[]) => void;
	attachments: unknown[];
	uploads: UploadItem[];
}

interface FileAttachmentDropDownState {
	hasRejectedFiles: boolean;
	allowedFileSizeTotalExceeded: boolean;
	totalSizeOfFiles: number;
	attachments: unknown[];
	uploads: UploadItem[];
}

class FileAttachmentDropDown extends Component<
	FileAttachmentDropDownProps,
	FileAttachmentDropDownState
> {
	private mounted = false;

	constructor(props: FileAttachmentDropDownProps) {
		super(props);

		this.onDrop = this.onDrop.bind(this);
		this.onProgress = this.onProgress.bind(this);
		this.onFailure = this.onFailure.bind(this);
		this.onClickRemove = this.onClickRemove.bind(this);
		this.uploadFile = this.uploadFile.bind(this);
		this.completeUpload = this.completeUpload.bind(this);
		this.cancelUpload = this.cancelUpload.bind(this);
		this.removeUploadAndAttachment = this.removeUploadAndAttachment.bind(this);
		this.createUpload = this.createUpload.bind(this);

		this.state = {
			hasRejectedFiles: false,
			allowedFileSizeTotalExceeded: false,
			totalSizeOfFiles: 0,
			attachments: props.attachments,
			uploads: props.uploads,
		};
	}

	componentDidMount() {
		this.mounted = true;
	}

	componentDidUpdate(prevProps: FileAttachmentDropDownProps) {
		if (
			prevProps.uploads !== this.props.uploads ||
			prevProps.attachments !== this.props.attachments
		) {
			this.setState({
				uploads: this.props.uploads,
				attachments: this.props.attachments,
			});
		}
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	private t(term: string): string {
		return translate(this.props.cultureCode, term);
	}

	private l(id: string): string {
		return link(this.props.cultureCode, id);
	}

	private onProgress(uploadId: string, percentage: number) {
		const { updateUploads } = this.props;
		const { uploads } = this.state;

		const uploadObj = uploads.find((bar) => bar.id === uploadId);
		if (!uploadObj) {
			return;
		}

		const formattedPercentage = percentage * 95;
		uploadObj.percentage = formattedPercentage;

		this.setState({ uploads });
		updateUploads(uploads);
	}

	private onFailure(uploadId: string) {
		const { updateUploads } = this.props;
		const { uploads, totalSizeOfFiles } = this.state;

		const uploadObj = uploads.find((bar) => bar.id === uploadId);
		if (!uploadObj) {
			return;
		}

		uploadObj.uploadFailed = true;
		uploadObj.uploadCompleted = false;
		uploadObj.percentage = 100;

		updateUploads(uploads);
		this.setState({
			uploads,
			totalSizeOfFiles: totalSizeOfFiles - uploadObj.fileSize,
		});
	}

	private onClickRemove(uploadId: string | undefined) {
		if (!uploadId) {
			return;
		}

		const { uploads } = this.state;
		const uploadObj = uploads.find((bar) => bar.id === uploadId);

		if (!uploadObj) {
			return;
		}

		if (
			!uploadObj.uploadCompleted &&
			!uploadObj.uploadFailed &&
			!uploadObj.uploadCancelled
		) {
			this.cancelUpload(uploadId);
			return;
		}

		this.removeUploadAndAttachment(uploadObj.attachmentId);
	}

	private normalizeRejectedFiles(
		rejectedFiles: Array<FileRejectionLike | File>,
	): File[] {
		return rejectedFiles
			.map((fileOrRejection) => {
				if (fileOrRejection instanceof File) {
					return fileOrRejection;
				}

				return fileOrRejection.file;
			})
			.filter((file): file is File => !!file);
	}

	private onDrop(
		acceptedFiles: File[],
		rejectedFiles: Array<FileRejectionLike | File>,
	) {
		const { totalSizeOfFiles } = this.state;

		const verifiedAcceptedFiles: File[] = [];
		const verifiedRejectedFiles: File[] = [];

		this.normalizeRejectedFiles(rejectedFiles).forEach((file) => {
			const extension = file.name.split(".").pop()?.toLowerCase() || "";

			if (extension === "heic" || extension === "heif") {
				verifiedAcceptedFiles.push(file);
			} else {
				verifiedRejectedFiles.push(file);
			}
		});

		acceptedFiles.forEach((file) => {
			const extension = file.name.split(".").pop()?.toLowerCase() || "";
			if (
				config.attachments.allowedFileExtensions.indexOf(`.${extension}`) === -1
			) {
				verifiedRejectedFiles.push(file);
			} else {
				verifiedAcceptedFiles.push(file);
			}
		});

		if (verifiedRejectedFiles.length) {
			this.setState({ hasRejectedFiles: true });
			return;
		}

		this.setState({ hasRejectedFiles: false });

		let totalFileSize = totalSizeOfFiles;
		const maxFileSizeTotal = config.attachments.maxSize;

		verifiedAcceptedFiles.forEach((file) => {
			totalFileSize += file.size;
		});

		if (totalFileSize > maxFileSizeTotal) {
			this.setState({ allowedFileSizeTotalExceeded: true });
			return;
		}

		this.setState({
			allowedFileSizeTotalExceeded: false,
			totalSizeOfFiles: totalFileSize,
		});

		verifiedAcceptedFiles.forEach((file, index) => {
			this.uploadFile(file, index);
		});
	}

	static setFileTypeFromExtension(file: File) {
		const ext = file.name.split(".").pop()?.toLowerCase() || "";
		if (ext === "heic" || ext === "heif") {
			return `image/${ext}`;
		}

		return "";
	}

	private uploadFile(file: File, index: number) {
		const { customer } = this.props;
		const uploadId = `${file.name}${index}${Date.now()}`;
		const formData = new FormData();

		const newFile = new File([file], file.name, {
			type: file.type || FileAttachmentDropDown.setFileTypeFromExtension(file),
		});

		formData.append("file", newFile);
		this.createUpload(newFile, uploadId);

		sendFileAttachment(customer, formData, uploadId, (percentage) =>
			this.onProgress(uploadId, percentage),
		)
			.then((attachmentsResponse) => {
				this.completeUpload(uploadId, attachmentsResponse);
			})
			.catch((error: { message?: string }) => {
				if (
					error.message === config.attachments.uploadCancelledByUserException
				) {
					return;
				}

				this.onFailure(uploadId);
			});
	}

	private completeUpload(
		uploadId: string,
		attachmentsResponse: UploadedAttachmentResponse,
	) {
		const { updateUploads, updateAttachments } = this.props;
		const { attachments, uploads } = this.state;

		const uploadObj = uploads.find((bar) => bar.id === uploadId);
		if (!uploadObj) {
			return;
		}

		attachments.push(attachmentsResponse);
		uploadObj.attachmentId = attachmentsResponse.AttachmentId;
		uploadObj.uploadCompleted = true;
		uploadObj.percentage = 100;

		updateUploads(uploads);
		updateAttachments(attachments);

		this.setState({ uploads, attachments });
	}

	private cancelUpload(uploadId: string) {
		const { updateUploads } = this.props;
		const { totalSizeOfFiles, uploads } = this.state;
		const uploadObj = uploads.find((bar) => bar.id === uploadId);

		if (!uploadObj) {
			return;
		}

		const cancelEvent = new CustomEvent("cancelUpload", {
			detail: { uploadId },
		});
		window.dispatchEvent(cancelEvent);

		uploadObj.uploadCancelled = true;
		uploadObj.percentage = 0;

		updateUploads(uploads);

		this.setState({
			uploads,
			totalSizeOfFiles: totalSizeOfFiles - uploadObj.fileSize,
		});
	}

	private createUpload(file: File, uploadId: string) {
		const { updateUploads } = this.props;
		const { uploads } = this.state;

		const newUploadObj: UploadItem = {
			fileName: file.name,
			fileSize: file.size,
			uploadFailed: false,
			uploadCancelled: false,
			uploadCompleted: false,
			percentage: 0,
			id: uploadId,
		};

		uploads.push(newUploadObj);

		updateUploads(uploads);
		this.setState({ uploads });
	}

	private removeUploadAndAttachment(attachmentId: string) {
		this.removeAttachment(attachmentId);
		this.removeUpload(attachmentId);
	}

	private removeAttachment(attachmentId: string) {
		const { updateAttachments } = this.props;
		const { attachments } = this.state;

		const attachment = attachments.find(
			(attachmentObj) =>
				typeof attachmentObj === "object" &&
				attachmentObj !== null &&
				"AttachmentId" in attachmentObj &&
				(attachmentObj as { AttachmentId: string }).AttachmentId ===
					attachmentId,
		);
		const attachmentIndex = attachments.indexOf(attachment as unknown);

		if (attachmentIndex >= 0) {
			attachments.splice(attachmentIndex, 1);
			updateAttachments(attachments);
			this.setState({ attachments });
		}
	}

	private removeUpload(attachmentId: string) {
		const { updateUploads } = this.props;
		const { totalSizeOfFiles, uploads } = this.state;

		const uploadObj = uploads.find(
			(upload) => upload.attachmentId === attachmentId,
		);
		if (!uploadObj) {
			return;
		}

		const uploadIndex = uploads.indexOf(uploadObj);
		uploads.splice(uploadIndex, 1);

		if (uploadObj.uploadCompleted) {
			this.setState({
				totalSizeOfFiles: totalSizeOfFiles - uploadObj.fileSize,
			});
		}

		updateUploads(uploads);
		this.setState({ uploads });
	}

	private showInfoTextSweden() {
		return this.props.cultureCode === culture.swedish;
	}

	render() {
		const { cultureCode, attachmentsDisabled } = this.props;
		const { hasRejectedFiles, allowedFileSizeTotalExceeded, uploads } =
			this.state;

		const accept = config.attachments.allowedFileTypes.reduce(
			(acc, type) => {
				acc[type] = [];
				return acc;
			},
			{} as Record<string, string[]>,
		);

		return (
			<React.Fragment>
				<div className="if row u-margin-bottom--smaller">
					<div className="if col-10--sm mypages-text--no-gutters">
						<h5 className="if heading">{this.t("AddingAttachmentsHeader")}</h5>
						<p>{this.t("AddingAttachmentsGeneralText")}</p>
						{this.showInfoTextSweden() && (
							<p>
								{this.t("AddingAttachmentInstructionsForClaimBeforeText")}
								<a href={this.l("ReportClaim_ExistingClaim")} className="if">
									{this.t("CategoryNotification_ExistingClaim_BannerLinkText")}
								</a>
								{this.t("AddingAttachmentInstructionsForClaimAfterText")}
							</p>
						)}
						<p>{this.t("AddingAttachmentsFileRequirementsText")}</p>
					</div>
				</div>
				<div className="if u-margin-bottom--smaller">
					<Dropzone
						onDrop={this.onDrop}
						accept={accept}
						disabled={attachmentsDisabled}
					>
						{({
							getRootProps,
							getInputProps,
							isDragActive,
							isDragAccept,
							isDragReject,
						}) => {
							let mainClassName = "mypages-attachment-dropzone";
							if (attachmentsDisabled)
								mainClassName += " mypages-attachment-dropzone--disabled";
							if (isDragActive)
								mainClassName += " mypages-attachment-dropzone--active";
							if (
								isDragReject ||
								hasRejectedFiles ||
								allowedFileSizeTotalExceeded
							) {
								mainClassName += " mypages-attachment-dropzone--rejected";
							}

							return (
								<div {...getRootProps()} className={mainClassName}>
									<input {...getInputProps()} />
									<div className="mypages-attachment-dropzone__drop-area">
										{!isDragActive && (
											<div className="mypages-attachment-dropzone__drop-area-text">
												<div className="mypages-attachment-dropzone__icon" />
												<span
													className="if u-hidden-down--smlr"
													style={{ paddingInlineEnd: "0.5rem" }}
												>
													{this.t("AddingAttachmentsDropAreaText")}
												</span>
												<button
													type="button"
													className="if button text"
													disabled={attachmentsDisabled}
												>
													{this.t("AddingAttachmentsDropAreaBrowseButtonText")}
												</button>
											</div>
										)}
										{isDragActive && (
											<React.Fragment>
												{isDragAccept &&
													this.t("AddingAttachmentsDropAreaDragAcceptText")}
												{isDragReject &&
													this.t("AddingAttachmentsDropAreaDragRejectText")}
											</React.Fragment>
										)}
									</div>
								</div>
							);
						}}
					</Dropzone>
				</div>
				{hasRejectedFiles && (
					<div className="if row u-margin-top--8 u-margin-bottom--8">
						<div className="if col-10--sm">
							<div className="mypages-attachment-dropzone__error-message">
								{this.t("AddingAttachmentsInvalidFileErrorMessage")}
							</div>
						</div>
					</div>
				)}
				{allowedFileSizeTotalExceeded && (
					<div className="if row u-margin-top--8 u-margin-bottom--8">
						<div className="if col-10--sm">
							<div className="mypages-attachment-dropzone__error-message">
								{this.t(
									"AddingAttachmentsInvalidTotalFileSizeExceededErrorMessage",
								)}
							</div>
						</div>
					</div>
				)}
				<UploadProgressBars
					onClickRemove={this.onClickRemove}
					cultureCode={cultureCode}
					uploads={uploads}
				/>
			</React.Fragment>
		);
	}
}

export default FileAttachmentDropDown;
